import { activateOffer, fetchOffer, retryOffer, waitForSubscriptionStatus } from "./api";
import { emitRecurringEvent } from "./events";
import { bindManage, isRetryable, manageBody } from "./manage";
import type { SubscriptionOffer } from "./types";
import {
  amountHero,
  chipRow,
  errorBox,
  hint,
  loadingRow,
  paymentShell,
  primaryButton,
} from "./ui";
import { connectAndApprove, prefetchWallet } from "./wallet";

function idFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("id")?.trim() || null;
}

function offerChips(offer: SubscriptionOffer): string {
  const items: Array<{ icon: string; label: string }> = [];
  if (offer.icon_coin_url && offer.token_symbol) {
    items.push({ icon: offer.icon_coin_url, label: offer.token_symbol });
  }
  const net = offer.short_name || offer.network_name;
  if (offer.icon_network_url && net) {
    items.push({ icon: offer.icon_network_url, label: net });
  }
  return chipRow(items);
}

export class SubscribeApp {
  private busy = false;

  constructor(private root: HTMLElement) {}

  async init(): Promise<void> {
    const id = idFromUrl();
    if (!id) {
      this.root.innerHTML = paymentShell({
        title: "Subscription",
        body: errorBox("Open this page as /subscribe?id=…"),
      });
      return;
    }
    this.root.innerHTML = paymentShell({
      title: "Subscription",
      body: loadingRow("Loading…"),
    });
    prefetchWallet();
    try {
      const offer = await fetchOffer(id);
      if (offer.status === "past_due") {
        await this.retryInBackground(offer);
        return;
      }
      this.render(offer);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to load offer";
      this.root.innerHTML = paymentShell({
        title: "Subscription",
        body: errorBox(msg),
      });
    }
  }

  private render(offer: SubscriptionOffer, extra = ""): void {
    const waiting = offer.status === "confirming";
    const managed = ["active", "past_due", "exhausted", "cancelled"].includes(offer.status);
    const amount = `${offer.amount_fiat} ${offer.currency_fiat}`;
    if (waiting) {
      this.root.innerHTML = paymentShell({
        title: offer.title,
        subtitle: "First payment",
        body: `
          ${amountHero(amount, "Subscription amount")}
          ${offerChips(offer)}
          ${loadingRow("Waiting for blockchain confirmation…")}
          ${hint("This can take a minute. Don’t close this window.")}
          ${extra}
        `,
      });
      return;
    }
    if (managed) {
      this.root.innerHTML = paymentShell({
        title: offer.title,
        subtitle: isRetryable(offer)
          ? `Retry the ${offer.token_symbol} payment`
          : `Manage your ${offer.token_symbol} subscription`,
        body: isRetryable(offer)
          ? `${amountHero(amount, "Subscription amount")}
            ${offerChips(offer)}
            ${manageBody(offer, extra)}`
          : manageBody(offer, extra),
      });
      bindManage(this.root, offer, (next) => {
        this.render(next);
        if (next.status === "confirming") {
          void this.waitUntilSettled(next);
        }
      });
      return;
    }

    this.root.innerHTML = paymentShell({
      title: offer.title,
      subtitle: `Approve a capped ${offer.token_symbol} allowance`,
      body: `
        ${amountHero(amount, "Subscription amount")}
        ${offerChips(offer)}
        ${primaryButton("go", "Connect wallet")}
        ${extra}
      `,
    });
    this.root.querySelector("#go")?.addEventListener("click", () => {
      void this.approve(offer);
    });
  }

  private async approve(offer: SubscriptionOffer): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.render(offer, loadingRow("Connect a wallet and confirm approve…"));
    try {
      const { payer, tx } = await connectAndApprove({
        chainId: offer.chain_id,
        token: offer.token_contract,
        spender: offer.spender,
        amountRaw: BigInt(offer.approve_amount_raw),
      });
      const next = await activateOffer(offer.subscription_id, payer, tx);
      if (next.status === "confirming") {
        emitRecurringEvent("subscription.confirming", next);
        this.render(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      this.render(next);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Approve failed";
      this.render(offer, errorBox(msg) + hint("Click Connect wallet again to retry."));
    }
  }

  private async retryInBackground(offer: SubscriptionOffer): Promise<void> {
    this.render(offer, loadingRow("Trying the payment again…"));
    try {
      const next = await retryOffer(offer.subscription_id);
      if (next.status === "confirming") {
        this.render(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      this.render(next);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Could not retry payment";
      this.render(offer, errorBox(msg));
    }
  }

  private async waitUntilSettled(offer: SubscriptionOffer): Promise<void> {
    try {
      const paid = await waitForSubscriptionStatus(offer.subscription_id, (item) =>
        ["active", "exhausted", "past_due", "cancelled"].includes(item.status)
      );
      this.busy = false;
      if (paid.status === "cancelled") {
        emitRecurringEvent("subscription.failed", paid);
        this.render(paid, errorBox(paid.last_error || "Payment was not confirmed"));
        return;
      }
      if (paid.status === "past_due") {
        emitRecurringEvent("subscription.failed", paid);
        this.render(paid);
        return;
      }
      emitRecurringEvent("subscription.active", paid);
      this.render(paid);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Confirmation timed out";
      this.render(offer, errorBox(msg));
    }
  }
}
